import { Button } from "@/components/core/buttons/button.tsx";
import {
	Form,
	FormField,
	FormGroup,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { type User, userSchema } from "@/features/dashboard/types/user.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useUserMutation } from "../api/queries/user-queries";

const UserProfileForm = ({ data }: { data?: User }) => {
	const userMutation = useUserMutation(data?.id);

	const form = useForm<User>({
		resolver: zodResolver(userSchema),
		defaultValues: {
			id: data?.id,
			first_name: data?.first_name || "",
			middle_name: data?.middle_name || "",
			last_name: data?.last_name || "",
			email: data?.email || "",
			status: data?.status || "Active",
			suffix: data?.suffix || "",
		},
	});

	const handleSubmit = async (data: User) => {
		await userMutation.mutateAsync(data);
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 gap-x-4 gap-y-3">
						<div className="col-span-12">
							<h3 className="font-bold text-xl">User Info</h3>
							<p className="text-sm text-gray-600">
								Information that shows on invoices and account when logging in.
								Primarily used for general contact information and
								identification.
							</p>
						</div>
					</div>

					<div className="grid grid-cols-12 gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="first_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>First Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="middle_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Middle Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="last_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Last Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="suffix"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Suffix</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Email Address</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="status"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Status</FormLabel>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														label: "Active",
														value: "Active",
													},
													{
														label: "Disabled",
														value: "Disabled",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-12 flex justify-end">
							<Button type="submit">Save</Button>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
};

export default UserProfileForm;
